import React, { useState } from 'react';
import { Folder, Pencil, Trash2, Plus } from 'lucide-react';

const HINT_FOCUS = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-paper-accent';

/**
 * Блок «Папки» над списком Истории: название и счётчик, переименование и удаление
 * по наведению (на телефоне кнопки видны всегда), «+ Новая папка».
 * Ничего не знает о сервисе: всё приходит пропсами. Ошибка из onCreate/onRename
 * показывается строкой под полем, само поле остаётся открытым.
 *
 * `hint` — подсказка о папках тем, у кого их ещё нет (срок и закрытие решает
 * владелец блока, lib/featureHint.js); о нажатиях сообщает onHintAction:
 * 'create' — «Создать папку», 'dismiss' — «Понятно».
 */
export default function HistoryFolders({ folders, activeId, mobile, onOpen, onCreate, onRename, onDelete, hint, onHintAction }) {
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(null); // папка, удаление которой подтверждают

  const closeField = () => { setCreating(false); setEditingId(null); setValue(''); setError(''); };

  const startCreate = () => { closeField(); setConfirming(null); setCreating(true); };
  const startRename = (e, f) => {
    e.stopPropagation();
    closeField(); setConfirming(null);
    setEditingId(f.id); setValue(f.name);
  };

  const submit = async () => {
    const name = value.trim();
    if (!name) { closeField(); return; }
    if (busy) return;
    const id = editingId;
    if (id && name === folders.find((f) => f.id === id)?.name) { closeField(); return; }
    setBusy(true); setError('');
    try {
      if (id) await onRename(id, name); else await onCreate(name);
      closeField();
    } catch (err) {
      setError(err?.message || 'Не получилось. Попробуйте ещё раз');
    } finally {
      setBusy(false);
    }
  };

  const onBlur = () => { if (!busy && !error && (!value.trim() || editingId)) closeField(); };

  const field = (
    <div className="px-3 py-1">
      <input
        autoFocus
        data-testid="folder-input"
        aria-label="Название папки"
        value={value}
        maxLength={64}
        placeholder="Название папки"
        onClick={(e) => e.stopPropagation()}
        onFocus={(e) => e.target.select()}
        onChange={(e) => { setValue(e.target.value); setError(''); }}
        onBlur={onBlur}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); submit(); }
          else if (e.key === 'Escape') { e.preventDefault(); closeField(); }
        }}
        className="w-full min-w-0 rounded-[6px] bg-paper-card px-[6px] py-[2px] text-[13px] text-paper-ink outline-none"
      />
      {error && <div className="mt-1 text-[12px] text-paper-accent">{error}</div>}
    </div>
  );

  const askDelete = (e, f) => {
    e.stopPropagation();
    if (mobile) {
      // На телефоне встроенное подтверждение негде разместить — обычное окно.
      if (window.confirm(`Удалить папку «${f.name}»? Записи останутся в Истории`)) onDelete(f);
      return;
    }
    closeField();
    setConfirming(f);
  };

  const doDelete = async () => {
    const f = confirming;
    setConfirming(null);
    await onDelete(f);
  };

  const showHint = Boolean(hint) && folders.length === 0;

  const btn = `${mobile ? 'w-9 h-9 -my-2 flex items-center justify-center' : 'opacity-0 group-hover:opacity-100 p-[2px]'} shrink-0 text-paper-hint`;

  return (
    <div data-testid="history-folders" className="mb-2">
      {showHint && (
        <div data-testid="folders-hint"
             className="mx-[2px] mb-2 rounded-control bg-paper-card px-3 pb-[10px] pt-[11px] text-[13px] leading-[1.45] text-paper-ink-2"
             style={{ boxShadow: 'inset 3px 0 0 var(--paper-accent, #E50071)' }}>
          <div className="mb-[2px] font-semibold text-paper-ink">Записи можно разложить по папкам</div>
          По проектам, клиентам или встречам — чтобы нужная расшифровка находилась сразу.
          <div className="mt-2 flex gap-[6px]">
            <button type="button" onClick={() => { startCreate(); onHintAction('create'); }}
                    className={`rounded-chip bg-paper-ink px-[10px] text-[12px] font-medium text-white transition-colors hover:bg-paper-ink-2 ${HINT_FOCUS} ${mobile ? 'min-h-[44px]' : 'py-[5px]'}`}>
              Создать папку
            </button>
            <button type="button" onClick={() => onHintAction('dismiss')}
                    className={`rounded-chip px-[10px] text-[12px] font-medium text-paper-ink-3 transition-colors hover:text-paper-ink ${HINT_FOCUS} ${mobile ? 'min-h-[44px]' : 'py-[5px]'}`}>
              Понятно
            </button>
          </div>
        </div>
      )}
      <div className="flex items-center gap-[6px] px-3 pb-1 text-[11px] font-semibold uppercase tracking-[0.05em] text-paper-ink-3">
        Папки
        {showHint && (
          <span className="rounded-badge bg-paper-accent px-[6px] py-[1px] text-[10px] tracking-[0.03em] text-white">Новое</span>
        )}
      </div>
      {folders.map((f) => (
        editingId === f.id ? (
          <div key={f.id} data-testid={`folder-row-${f.id}`}>{field}</div>
        ) : (
          <div
            key={f.id}
            data-testid={`folder-row-${f.id}`}
            onClick={() => onOpen(f.id)}
            className={`group flex items-center gap-[6px] rounded-[10px] px-3 py-2 cursor-pointer hover:bg-paper-card ${activeId === f.id ? 'bg-paper-card' : ''}`}
          >
            <Folder className="h-3.5 w-3.5 shrink-0 text-paper-ink-3" />
            <div title={f.name} className="flex-1 min-w-0 truncate text-[13px] font-medium text-paper-ink">{f.name}</div>
            <span className="shrink-0 text-[11px] text-paper-ink-3">{f.count}</span>
            {!confirming && (
              <>
                <button onClick={(e) => startRename(e, f)} title="Переименовать" aria-label="Переименовать"
                        className={`${btn} hover:text-paper-ink`}>
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button onClick={(e) => askDelete(e, f)} title="Удалить папку" aria-label="Удалить папку"
                        className={`${btn} hover:text-paper-accent`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </>
            )}
          </div>
        )
      ))}
      {confirming && (
        <div className="mx-1 my-1 rounded-[10px] bg-paper-card px-3 py-2 text-[12px] leading-[1.5] text-paper-ink-2">
          <div>Удалить папку «{confirming.name}»? Записи останутся в Истории</div>
          <div className="mt-2 flex gap-2">
            <button onClick={() => setConfirming(null)} className="rounded-[8px] px-2 py-1 text-paper-ink-3 hover:text-paper-ink">Отмена</button>
            <button onClick={doDelete} className="rounded-[8px] px-2 py-1 font-medium text-paper-accent">Удалить папку</button>
          </div>
        </div>
      )}
      {creating ? field : (
        <button
          data-testid="folder-new"
          onClick={startCreate}
          className="flex w-full items-center gap-[6px] rounded-[10px] px-3 py-2 text-left text-[13px] text-paper-ink-3 hover:bg-paper-card hover:text-paper-ink"
        >
          <Plus className="h-3.5 w-3.5 shrink-0" />
          Новая папка
        </button>
      )}
    </div>
  );
}
